import {
  Calendar03Icon,
  Cancel01Icon,
  PinOffIcon,
  RepeatIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { Checkbox, Tooltip, useConfirmModal } from '@pique/component';
import {
  adaptSerializedAttendees,
  AttendeeAvatars,
} from '@pique/core/components/pique/page-properties/attendee-avatars';
import { openActionItemInKanbanSlot } from '@pique/core/components/blocksuite/block-suite-editor/specs/custom/pique/pique-slots';
import {
  ActionItemsService,
  DEFAULT_PLANNED_FOCUS_MINUTES,
  formatDueDateForDisplay,
  isDueDateOverdue,
  isFocusScheduleOverdue,
  NO_DUE_DATE_KEY,
  OVERDUE_COLOR,
  parseDueDateToKey,
} from '@pique/core/modules/properties/services/action-items-service';
import {
  ACTION_ITEM_STATUS,
  type ActionItemPriority,
  ActionItemPrioritySchema,
  type ActionItemStatus,
  type SerializedTodoItem,
} from '@pique/core/modules/properties/services/schema';
import { WorkbenchService } from '@pique/core/modules/workbench';
import { posthog } from '@pique/core/posthog';
import { useLiveData, useService } from '@toeverything/infra';
import clsx from 'clsx';
import { format } from 'date-fns';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';

import {
  ActionItemDatePicker,
  type QuickDateOption,
} from './ActionItemDatePicker';
import {
  getStatusColor,
  getStatusConfig,
  getVisualProgress,
} from './config/status-config';
import {
  extractRepeatDateFromEventId,
  isRecurringEventCompleted,
} from './focus-mode-utils';
import { LoopProgressRing } from './LoopProgressRing';
import { PomodoroTimer } from './PomodoroTimer';
import { ScheduleMePopup } from './ScheduleMePopup';
import * as styles from './TodoItemComponent.css';

export interface TodoItemProps {
  item: SerializedTodoItem;
  onClick?: () => void;
  onComplete?: (itemId: string, pageId: string) => void;
  isDraggable?: boolean;
  isNew?: boolean;
  compact?: boolean;
  selectable?: boolean;
  selected?: boolean;
  onSelectedChange?: () => void;
  calendarEvent?: boolean;
  calendarEventId?: string;
  backgroundColor?: string;
  dimmed?: boolean;
  dragging?: boolean;
  isResizing?: boolean;
  plannedDurationMinutes?: number;
  onOpenCalendarView?: (targetDate?: Date, itemId?: string) => void;
  onClearSchedule?: (itemId: string) => void;
  onExpandToWeek?: (itemId: string) => void;
  onCollapseToDay?: (itemId: string) => void;
  isHighlighted?: boolean;
  isNarrowColumn?: boolean;
  suppressFocusZone?: boolean;
  suppressControls?: boolean;
}

export const TodoItemComponent: React.FC<TodoItemProps> = ({
  item,
  onClick,
  onComplete,
  isDraggable = false,
  isNew = false,
  compact = false,
  selectable = false,
  selected = false,
  onSelectedChange,
  calendarEvent = false,
  calendarEventId,
  backgroundColor,
  dimmed = false,
  dragging = false,
  isResizing = false,
  plannedDurationMinutes: plannedDurationMinutesProp,
  onOpenCalendarView,
  onClearSchedule,
  onExpandToWeek,
  onCollapseToDay,
  isHighlighted = false,
  suppressFocusZone = false,
  suppressControls = false,
}) => {
  const actionItemsService = useService(ActionItemsService);
  const workbenchService = useService(WorkbenchService);
  const { openConfirmModal } = useConfirmModal();
  const focusedItemId = useLiveData(actionItemsService.focusedItemId$);
  const focusedSessionStartedAtMs = useLiveData(
    actionItemsService.focusedSessionStartedAtMs$
  );
  const isInFocusZone = suppressFocusZone ? false : Boolean(item.isFocused);
  const isActiveFocus = focusedItemId === item.id;
  const showCalendarDragTime = Boolean(
    calendarEvent && (dragging || isResizing) && item.scheduledAt
  );
  const plannedDurationMinutes = useMemo(() => {
    const candidate =
      typeof plannedDurationMinutesProp === 'number'
        ? plannedDurationMinutesProp
        : item.plannedDuration;
    if (!Number.isFinite(candidate) || !candidate || candidate <= 0) {
      return DEFAULT_PLANNED_FOCUS_MINUTES;
    }
    return Math.round(candidate);
  }, [item.plannedDuration, plannedDurationMinutesProp]);

  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [timerMenuOpen, setTimerMenuOpen] = useState(false);
  const isVisuallyComplete = false;

  const [showDuePicker, setShowDuePicker] = useState(false);
  const breakdownSteps = useMemo(() => {
    const raw = (item as SerializedTodoItem).breakdownSteps;
    return Array.isArray(raw)
      ? raw.map(step => ({
          id: String(step.id),
          title:
            typeof step.title === 'string'
              ? step.title
              : String(step.title ?? ''),
          description:
            typeof step.description === 'string' ? step.description : '',
          completed: Boolean(step.completed),
        }))
      : [];
  }, [item]);

  const dueDateKey = parseDueDateToKey(item.dueDate);
  const formattedDueDate = formatDueDateForDisplay(item.dueDate);
  const isOverdue = isDueDateOverdue(item.dueDate);
  const isNoDate = dueDateKey === NO_DUE_DATE_KEY;
  const tomorrowKey = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return parseDueDateToKey(d);
  }, []);
  const nextWeekKey = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return parseDueDateToKey(d);
  }, []);

  const hasBreakdownSteps = breakdownSteps.length > 0;
  const stepsCompletedCount = breakdownSteps.filter(s => s.completed).length;
  const stepsProgress = hasBreakdownSteps
    ? Math.round((stepsCompletedCount / breakdownSteps.length) * 100)
    : 0;
  const progress = hasBreakdownSteps ? stepsProgress : item.progress || 0;

  const statusColor = getStatusColor(item.status);
  const visualProgress = hasBreakdownSteps
    ? stepsProgress
    : getVisualProgress(item.status, progress, isVisuallyComplete);
  const isRecurringInstance = Boolean(
    calendarEventId && calendarEventId.includes(':repeat:')
  );
  const recurringDateCompleted = isRecurringInstance
    ? isRecurringEventCompleted(calendarEventId!, item.completedDates)
    : false;
  const isComplete = isRecurringInstance
    ? recurringDateCompleted
    : visualProgress >= 100;
  const isScheduleOverdue = isFocusScheduleOverdue(item);
  const calendarAccentColor = calendarEvent
    ? isInFocusZone
      ? 'var(--pique-green-primary)'
      : isComplete
        ? 'var(--pique-green-primary)'
        : 'var(--affine-brand-color)'
    : undefined;
  const showButtonTooltips = false;

  const navigateToActionItem = useCallback(() => {
    const pageId = item.pageId;
    if (pageId) {
      posthog.capture('action_item_opened', {
        item_id: item.id,
        page_id: pageId,
      });
      workbenchService.workbench.openDoc(
        { docId: pageId, mode: 'page' },
        {
          at: 'active',
          onComplete: () => {
            openActionItemInKanbanSlot.emit({ pageId, itemId: item.id });
          },
        }
      );
    } else if (onClick) {
      onClick();
    }
  }, [item, workbenchService, onClick]);

  const handleItemClick = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'BUTTON' ||
        target.tagName === 'INPUT' ||
        target.tagName === 'SELECT' ||
        target.tagName === 'TEXTAREA'
      ) {
        return;
      }
      navigateToActionItem();
    },
    [navigateToActionItem]
  );

  const handleMarkComplete = useCallback(() => {
    if (!item.id) return;

    const priority = (item.priority ??
      ActionItemPrioritySchema.enum.Medium) as ActionItemPriority;

    if (isRecurringInstance && calendarEventId) {
      const dateKey = extractRepeatDateFromEventId(calendarEventId);
      if (!dateKey) return;

      const currentCompleted = item.completedDates?.[dateKey] ?? false;
      const updatedCompletedDates = {
        ...item.completedDates,
        [dateKey]: !currentCompleted,
      };

      if (currentCompleted) {
        posthog.capture('action_item_status_changed', { item_id: item.id, priority });
      } else {
        posthog.capture('action_item_completed', {
          item_id: item.id,
          priority,
        });
      }

      actionItemsService.updateById(item.id, {
        completedDates: updatedCompletedDates,
      });
      return;
    }

    if (visualProgress >= 100) {
      const prevStatus = typeof item.status === 'string' ? item.status : '';
      const newStatus =
        prevStatus &&
        getStatusConfig(prevStatus).displayName === ACTION_ITEM_STATUS.DONE
          ? (ACTION_ITEM_STATUS.IN_PROGRESS as ActionItemStatus)
          : (prevStatus as ActionItemStatus) ||
            (ACTION_ITEM_STATUS.TODO as ActionItemStatus);

      posthog.capture('action_item_status_changed', { item_id: item.id, priority });

      if (hasBreakdownSteps) {
        const uncompletedSteps = breakdownSteps.map(step => ({
          ...step,
          completed: false,
        }));
        actionItemsService.updateById(item.id, {
          breakdownSteps: uncompletedSteps,
          progress: 0,
          status: newStatus,
        });
      } else {
        actionItemsService.updateById(item.id, {
          progress: 0,
          status: newStatus,
        });
      }

      return;
    }

    posthog.capture('action_item_completed', { item_id: item.id, priority });

    setTimeout(() => {
      if (hasBreakdownSteps) {
        const completedSteps = breakdownSteps.map(step => ({
          ...step,
          completed: true,
        }));
        actionItemsService.updateById(item.id, {
          breakdownSteps: completedSteps,
          progress: 100,
          status: ACTION_ITEM_STATUS.DONE as ActionItemStatus,
        });
      } else {
        actionItemsService.updateById(item.id, {
          progress: 100,
          status: ACTION_ITEM_STATUS.DONE as ActionItemStatus,
        });
      }

      if (onComplete) {
        onComplete(item.id, item.pageId || '');
      }
    }, 700);
  }, [
    item,
    actionItemsService,
    onComplete,
    visualProgress,
    isRecurringInstance,
    calendarEventId,
    hasBreakdownSteps,
    breakdownSteps,
  ]);

  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!item.id) return;

      openConfirmModal({
        title: 'Delete action item',
        description:
          'This moves the action item to trash so it can be restored.',
        confirmText: 'Delete',
        confirmButtonOptions: {
          variant: 'error',
        },
        onConfirm: () => {
          posthog.capture('action_item_deleted', {
            item_id: item.id,
            status: String(item.status || ACTION_ITEM_STATUS.TODO),
          });
          actionItemsService.removeById(item.id);
        },
      });
    },
    [item, actionItemsService, openConfirmModal]
  );

  const handleDueDatePickerOpen = useCallback(() => {
    setShowDuePicker(v => !v);
  }, []);

  const handleQuickDateSelect = useCallback(
    (option: string, date: string) => {
      setShowDuePicker(false);
      posthog.capture('action_item_due_date_set', {
        item_id: item.id,
        option,
        new_due_date: date,
      });
      if (item.id) {
        actionItemsService.updateById(item.id, { dueDate: date });
      }
    },
    [item.id, actionItemsService]
  );

  const handleCalendarDateSelect = useCallback(
    (date: string) => {
      setShowDuePicker(false);
      posthog.capture('action_item_due_date_set', {
        item_id: item.id,
        new_due_date: date,
      });
      if (item.id) {
        actionItemsService.updateById(item.id, { dueDate: date });
      }
    },
    [item.id, actionItemsService]
  );

  const handleMoveToBacklog = useCallback(() => {
    setShowDuePicker(false);
    if (!item.id) return;
    posthog.capture('action_item_status_changed', { item_id: item.id });
    actionItemsService.updateById(item.id, { dueDate: null });
  }, [item.id, actionItemsService]);

  const quickDueDateOptions = useMemo(
    (): QuickDateOption[] => [
      {
        label: 'Tomorrow',
        selected: dueDateKey === tomorrowKey,
        onClick: () => handleQuickDateSelect('tomorrow', tomorrowKey),
      },
      {
        label: 'Next week',
        selected: dueDateKey === nextWeekKey,
        onClick: () => handleQuickDateSelect('next_week', nextWeekKey),
      },
      {
        label: 'Sometime later',
        selected: isNoDate,
        onClick: handleMoveToBacklog,
      },
    ],
    [
      dueDateKey,
      tomorrowKey,
      nextWeekKey,
      isNoDate,
      handleQuickDateSelect,
      handleMoveToBacklog,
    ]
  );

  const formatScheduledTime = (isoString: string | undefined): string => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      return format(date, 'h:mm aaa');
    } catch {
      return '';
    }
  };

  const formatCompactTime = (date: Date): string => {
    const raw = format(date, 'h:mm a');
    const parts = raw.split(' ');
    const time = parts[0] ?? '';
    const meridiem = parts[1] ?? '';
    const compactTime = time.endsWith(':00') ? time.slice(0, -3) : time;
    return `${compactTime}${meridiem.toLowerCase()}`;
  };

  const formatScheduledRange = (
    isoString: string | undefined,
    durationMinutes: number
  ): string => {
    if (!isoString) return '';
    const startDate = new Date(isoString);
    if (Number.isNaN(startDate.getTime())) return '';
    const safeDuration = Math.max(1, Math.floor(durationMinutes));
    const endDate = new Date(startDate.getTime() + safeDuration * 60000);
    return `${formatCompactTime(startDate)} - ${formatCompactTime(endDate)}`;
  };

  const anyMenuOpen = showDuePicker || timerMenuOpen;

  const handleCheckboxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      onSelectedChange?.();
    },
    [onSelectedChange]
  );

  const dragTimeRange = useMemo(
    () => formatScheduledRange(item.scheduledAt, plannedDurationMinutes),
    [item.scheduledAt, plannedDurationMinutes]
  );

  const isShortEvent = calendarEvent && plannedDurationMinutes <= 30;
  const isTinyEvent = calendarEvent && plannedDurationMinutes <= 15;

  return (
    <div
      className={styles.todoItem}
      data-testid="todo-item"
      data-compact={compact || undefined}
      data-menu-open={anyMenuOpen || undefined}
      data-selectable={selectable || undefined}
      data-calendar-event={calendarEvent || undefined}
      data-in-focus-zone={isInFocusZone || undefined}
      data-schedule-overdue={isScheduleOverdue || undefined}
      data-timer-running={isTimerRunning || undefined}
      data-dragging={dragging || undefined}
      data-drag-preview={showCalendarDragTime || undefined}
      data-highlighted={isHighlighted || undefined}
      data-week-view={onCollapseToDay ? true : undefined}
      data-short-event={isShortEvent || undefined}
      data-tiny-event={isTinyEvent || undefined}
      style={
        {
          background: calendarEvent
            ? undefined
            : isComplete
              ? `${statusColor}0D`
              : undefined,
          '--status-bg-color': `${statusColor}08`,
          '--calendar-accent-color': calendarAccentColor,
          '--calendar-event-bg': backgroundColor ?? undefined,
          cursor: calendarEvent ? undefined : 'default',
        } as React.CSSProperties
      }
    >
      <div className={styles.todoItemInner}>
        {calendarEvent &&
          onClearSchedule &&
          (showButtonTooltips ? (
            <Tooltip content="Unschedule" side="top" align="center">
              <button
                type="button"
                className={styles.clearScheduleButton}
                onClick={e => {
                  e.stopPropagation();
                  onClearSchedule(item.id);
                }}
              >
                <HugeiconsIcon icon={PinOffIcon} size={10} strokeWidth={2} />
              </button>
            </Tooltip>
          ) : (
            <button
              type="button"
              className={styles.clearScheduleButton}
              onClick={e => {
                e.stopPropagation();
                onClearSchedule(item.id);
              }}
            >
              <HugeiconsIcon icon={PinOffIcon} size={10} strokeWidth={2} />
            </button>
          ))}
        {/* Repeat button - week view or recurring items in day view */}
        {(onCollapseToDay || (calendarEvent && item.repeatConfig)) && (
          <button
            type="button"
            className={styles.repeatButton}
            onClick={e => e.stopPropagation()}
            data-has-repeat={item.repeatConfig ? true : undefined}
            title={item.repeatConfig ? 'Recurring task' : 'Repeat'}
          >
            <HugeiconsIcon icon={RepeatIcon} size={12} strokeWidth={2} />
          </button>
        )}
        {selectable && (
          <div
            className={clsx(
              styles.selectionCell,
              selected && styles.selectionCellVisible
            )}
            onClick={e => e.stopPropagation()}
          >
            <Checkbox checked={selected} onChange={handleCheckboxChange} />
          </div>
        )}
        {isDraggable ? (
          <div className={styles.dragHandle} aria-hidden="true">
            <svg
              width="16"
              height="23"
              viewBox="0 0 16 23"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.33334 5.75H5.33758M5.33334 11.5H5.33758M5.33334 17.25H5.33758M10.6625 5.75H10.6667M10.6625 11.5H10.6667M10.6625 17.25H10.6667"
                stroke="var(--affine-text-secondary-color)"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        ) : null}
        {!suppressControls && (
          <div
            className={styles.todoItemLeftSection}
            data-calendar-time={showCalendarDragTime ? 'true' : undefined}
          >
            {calendarEvent && showCalendarDragTime && (
              <div
                className={styles.focusScheduledTime}
                data-has-time="true"
                title={formatScheduledTime(item.scheduledAt!)}
              >
                {formatScheduledTime(item.scheduledAt!)}
              </div>
            )}
            {!calendarEvent && (
              <ScheduleMePopup
                item={item}
                isInFocusZone={isInFocusZone}
                calendarEvent={calendarEvent}
                isScheduleOverdue={isScheduleOverdue}
                onOpenCalendarView={onOpenCalendarView}
              />
            )}
            <LoopProgressRing
              progress={visualProgress}
              size={18}
              stroke={2}
              onClick={handleMarkComplete}
              itemId={item.id}
              pageId={item.pageId}
              isFocused={isInFocusZone}
              statusColor={statusColor}
            />
          </div>
        )}

        {/* Middle Content - Title and Metadata */}
        <div className={styles.todoItemContent}>
          {calendarEvent ? (
            <div className={styles.calendarEventHeader}>
              <div className={styles.calendarEventText}>
                <div
                  className={clsx(
                    styles.todoItemLabel,
                    styles.calendarEventTitle
                  )}
                  onClick={
                    onCollapseToDay
                      ? e => {
                          e.stopPropagation();
                          onCollapseToDay(item.id);
                        }
                      : item.pageId && !calendarEvent
                        ? handleItemClick
                        : undefined
                  }
                  style={{
                    color: dimmed
                      ? 'var(--affine-text-secondary-color)'
                      : undefined,
                    textDecoration: isComplete ? 'line-through' : 'none',
                    opacity: isComplete ? 0.85 : 1,
                    transition: 'opacity 0.3s ease, text-decoration 0.2s ease',
                    cursor: onCollapseToDay
                      ? 'pointer'
                      : item.pageId && !calendarEvent
                        ? 'pointer'
                        : 'grab',
                  }}
                >
                  {typeof item.title === 'string'
                    ? item.title
                    : String(item.title || 'Untitled Task')}
                </div>
                {showCalendarDragTime && (
                  <div className={styles.dragTimeGroup}>
                    <div
                      className={styles.dragTimePreview}
                      title={
                        dragTimeRange || formatScheduledTime(item.scheduledAt)
                      }
                    >
                      {dragTimeRange || formatScheduledTime(item.scheduledAt)}
                    </div>
                  </div>
                )}
              </div>
              {!dragging && (
                <>
                  <PomodoroTimer
                    item={item}
                    isInFocusZone={isInFocusZone}
                    isActiveFocus={isActiveFocus}
                    focusedSessionStartedAtMs={focusedSessionStartedAtMs}
                    onTimerActiveChange={setIsTimerRunning}
                    onMenuOpenChange={setTimerMenuOpen}
                    showTooltips={showButtonTooltips}
                    menuMode="inline"
                  />
                  {onExpandToWeek && (
                    <button
                      type="button"
                      className={styles.expandToWeekButton}
                      onClick={e => {
                        e.stopPropagation();
                        onExpandToWeek(item.id);
                      }}
                      title="View in week"
                    >
                      <HugeiconsIcon icon={Calendar03Icon} size={14} />
                    </button>
                  )}
                </>
              )}
            </div>
          ) : (
            <>
              {/* Title */}
              <div
                className={styles.todoItemLabel}
                onClick={
                  item.pageId && !calendarEvent ? handleItemClick : undefined
                }
                style={{
                  color: dimmed
                    ? 'var(--affine-text-secondary-color)'
                    : undefined,
                  textDecoration: isComplete ? 'line-through' : 'none',
                  opacity: isComplete ? 0.85 : 1,
                  transition: 'opacity 0.3s ease, text-decoration 0.2s ease',
                  cursor:
                    item.pageId && !calendarEvent
                      ? 'pointer'
                      : calendarEvent
                        ? 'grab'
                        : 'default',
                }}
              >
                {typeof item.title === 'string'
                  ? item.title
                  : String(item.title || 'Untitled Task')}
              </div>

              {!dragging && !suppressControls ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '20px',
                    marginTop: '2px',
                  }}
                >
                  <PomodoroTimer
                    item={item}
                    isInFocusZone={isInFocusZone}
                    isActiveFocus={isActiveFocus}
                    focusedSessionStartedAtMs={focusedSessionStartedAtMs}
                    onTimerActiveChange={setIsTimerRunning}
                    onMenuOpenChange={setTimerMenuOpen}
                    showTooltips={showButtonTooltips}
                  />
                  <div className={styles.todoItemMetadata}>
                    <ActionItemDatePicker
                      open={showDuePicker}
                      onOpenChange={setShowDuePicker}
                      quickOptions={quickDueDateOptions}
                      sectionLabel="Due date:"
                      value={isNoDate ? undefined : dueDateKey}
                      onChange={handleCalendarDateSelect}
                      noDateSelected={isNoDate}
                      trigger={
                        <button
                          className={styles.todoItemDate}
                          onClick={e => {
                            e.stopPropagation();
                            handleDueDatePickerOpen();
                          }}
                          title="Set due date"
                          style={{
                            color:
                              isOverdue && !isComplete
                                ? OVERDUE_COLOR
                                : 'var(--affine-text-secondary-color)',
                          }}
                        >
                          {`DUE ${formattedDueDate || 'Later'}`}
                        </button>
                      }
                    />
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>

        {/* Right Side - Attendees and Action Menu */}
        <div className={styles.todoItemActions}>
          {/* Quick actions cluster (top-right) */}
          <div className={styles.quickActionsContainer}>
            {/* Open button - navigates to page and opens detail panel */}
            {item.pageId &&
              (showButtonTooltips ? (
                <Tooltip content="Open" side="top">
                  <button
                    className={styles.openButton}
                    onClick={e => {
                      e.stopPropagation();
                      navigateToActionItem();
                    }}
                    aria-label="Open action item"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      width="10"
                      height="10"
                      fill="none"
                    >
                      <g transform="rotate(45 12 12)">
                        <g transform="translate(0 -1)">
                          <path
                            fill="currentColor"
                            stroke="currentColor"
                            strokeWidth="0.3"
                            d="M17.434,10.49 C15.2,7.965 12.66,5.751 11.999,5.751 C11.337,5.751 8.799,7.965 6.565,10.49 C6.289,10.8 5.817,10.828 5.506,10.555 C5.196,10.281 5.166,9.808 5.441,9.496 C5.486,9.444 6.581,8.211 7.889,6.955 C10.388,4.558 11.397,4.25 11.999,4.25 C12.6,4.25 13.608,4.557 16.108,6.955 C17.416,8.211 18.511,9.444 18.556,9.496 C18.83,9.806 18.801,10.28 18.492,10.555 C18.349,10.681 18.172,10.742 17.997,10.743 L17.995,10.743 C17.788,10.742 17.583,10.657 17.434,10.49 Z"
                          />
                        </g>
                        <g transform="translate(0 1)">
                          <path
                            fill="currentColor"
                            stroke="currentColor"
                            strokeWidth="0.3"
                            d="M7.892,17.036 C6.599,15.792 5.517,14.569 5.446,14.489 L5.444,14.487 C5.17,14.177 5.2,13.704 5.51,13.43 C5.821,13.156 6.294,13.186 6.569,13.496 C8.802,16.028 11.34,18.249 12.001,18.249 C12.663,18.249 15.203,16.027 17.436,13.496 C17.709,13.185 18.183,13.156 18.494,13.43 C18.804,13.704 18.834,14.177 18.56,14.487 L18.558,14.489 C18.488,14.569 17.405,15.792 16.112,17.036 C13.612,19.44 12.603,19.748 12.001,19.748 C11.401,19.748 10.391,19.44 7.892,17.036 Z"
                          />
                        </g>
                      </g>
                    </svg>
                  </button>
                </Tooltip>
              ) : (
                <button
                  className={styles.openButton}
                  onClick={e => {
                    e.stopPropagation();
                    navigateToActionItem();
                  }}
                  aria-label="Open action item"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="10"
                    height="10"
                    fill="none"
                  >
                    <g transform="rotate(45 12 12)">
                      <g transform="translate(0 -1)">
                        <path
                          fill="currentColor"
                          stroke="currentColor"
                          strokeWidth="0.3"
                          d="M17.434,10.49 C15.2,7.965 12.66,5.751 11.999,5.751 C11.337,5.751 8.799,7.965 6.565,10.49 C6.289,10.8 5.817,10.828 5.506,10.555 C5.196,10.281 5.166,9.808 5.441,9.496 C5.486,9.444 6.581,8.211 7.889,6.955 C10.388,4.558 11.397,4.25 11.999,4.25 C12.6,4.25 13.608,4.557 16.108,6.955 C17.416,8.211 18.511,9.444 18.556,9.496 C18.83,9.806 18.801,10.28 18.492,10.555 C18.349,10.681 18.172,10.742 17.997,10.743 L17.995,10.743 C17.788,10.742 17.583,10.657 17.434,10.49 Z"
                        />
                      </g>
                      <g transform="translate(0 1)">
                        <path
                          fill="currentColor"
                          stroke="currentColor"
                          strokeWidth="0.3"
                          d="M7.892,17.036 C6.599,15.792 5.517,14.569 5.446,14.489 L5.444,14.487 C5.17,14.177 5.2,13.704 5.51,13.43 C5.821,13.156 6.294,13.186 6.569,13.496 C8.802,16.028 11.34,18.249 12.001,18.249 C12.663,18.249 15.203,16.027 17.436,13.496 C17.709,13.185 18.183,13.156 18.494,13.43 C18.804,13.704 18.834,14.177 18.56,14.487 L18.558,14.489 C18.488,14.569 17.405,15.792 16.112,17.036 C13.612,19.44 12.603,19.748 12.001,19.748 C11.401,19.748 10.391,19.44 7.892,17.036 Z"
                        />
                      </g>
                    </g>
                  </svg>
                </button>
              ))}
          </div>

          {/* Row 2: Avatars */}
          {((item.assignedTo && item.assignedTo.length > 0) || isNew) && (
            <div className={styles.actionsBottomRow}>
              {item.assignedTo && item.assignedTo.length > 0 && (
                <AttendeeAvatars
                  attendees={adaptSerializedAttendees(item.assignedTo)}
                  maxDisplay={2}
                  size="small"
                  showTooltips={false}
                />
              )}
              {isNew && <div className={styles.newBadge}>New</div>}
            </div>
          )}
          {/* Delete button */}
          {showButtonTooltips ? (
            <Tooltip content="Delete" side="top">
              <button
                type="button"
                className={styles.deleteButton}
                onClick={handleDelete}
                aria-label="Delete action item"
              >
                <HugeiconsIcon icon={Cancel01Icon} size={10} />
              </button>
            </Tooltip>
          ) : (
            <button
              type="button"
              className={styles.deleteButton}
              onClick={handleDelete}
              aria-label="Delete action item"
            >
              <HugeiconsIcon icon={Cancel01Icon} size={10} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
